import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';

// ---------------------------------------------------------------------------
// Embedded: Classification result from the Reply Agent
// ---------------------------------------------------------------------------

@Entity()
export class ClassifyResultDoc extends MongoEntity {
  static override _class = 'ClassifyResultDoc';

  /** 'interested' | 'objection' | 'question' | 'opt_out' | 'positive' | 'neutral' | 'off_topic' */
  @Field(type => String) replyIntent!: string;
  /** 'positive' | 'neutral' | 'negative' */
  @Field(type => String) sentiment!: string;
  /** Whether the campaign prompt's CTA is appropriate given the reply */
  @Field(type => Boolean) promptActionAppropriate!: boolean;
  /** Agent's reasoning (human-readable) */
  @Field(type => String) reasoning!: string;
  /** 'push_cta' | 'soft_cta' | 'address_objection' | 'answer_question' | 'acknowledge_exit' | 'follow_up_neutral' */
  @Field(type => String) suggestedStrategy!: string;
}

// ---------------------------------------------------------------------------
// Embedded: Single conversation message for history tracking
// ---------------------------------------------------------------------------

@Entity()
export class ConversationMessageDoc extends MongoEntity {
  static override _class = 'ConversationMessageDoc';

  /** 'agent' | 'lead' */
  @Field(type => String) role!: string;
  @Field(type => String) message!: string;
  @Field(type => String) timestamp!: string;
}

// ---------------------------------------------------------------------------
// ReplyDraft — An AI-generated draft response awaiting user review
// ---------------------------------------------------------------------------

@Entity()
export class ReplyDraft extends MongoEntity {
  static override _class = 'ReplyDraft';
  static override _collectionName = 'reply_drafts';

  @Field(type => String) campaignId!: string;
  @Field(type => String) userId!: string;
  @Field(type => String) leadId!: string;
  /** 'email' | 'linkedin_dm' */
  @Field(type => String) channel!: string;
  @Field(type => Int) stepIndex?: number;
  @Field(type => String) stepId?: string;

  /** The lead's reply text that triggered this draft */
  @Field(type => String) customerReply!: string;

  /** Full conversation history at the time of draft generation */
  @Field(type => [ConversationMessageDoc]) conversationHistory!: ConversationMessageDoc[];

  /** Classification result from Phase 1 (Classify) */
  @Field(type => ClassifyResultDoc) classifyResult!: ClassifyResultDoc;

  /** Generated draft message (Phase 2) */
  @Field(type => String) draftMessage!: string;
  /** Generated draft subject (email only) */
  @Field(type => String) draftSubject?: string;

  /** User-edited message (set when user edits before approving) */
  @Field(type => String) editedMessage?: string;

  /**
   * 'pending_review' | 'approved' | 'queued' | 'sent' | 'send_failed' | 'rejected' | 'edited'
   * 'queued' means a LinkedIn DM job was created but not yet delivered — only the
   * tick worker's own success/failure of that job flips this to 'sent'/'send_failed',
   * so the UI never shows "Sent" before the message actually went out.
   */
  @Field(type => String) status!: string;

  @Field(type => String) generatedAt!: string;
  @Field(type => String) reviewedAt?: string;
  @Field(type => String) sentAt?: string;
}
